import { useQueryClient } from "@tanstack/react-query";
import { usePrompt } from "../components/PromptDialog";
import { api } from "./api";

// Double-click-to-rename helper, shared by the sidebar rows and the tab
// pills. Pops a prompt with the current name, on confirm PATCHes the request
// (which also creates a revision with note "renamed") and invalidates every
// view that displays the name.
export function useRenameRequest() {
  const prompt = usePrompt();
  const qc = useQueryClient();
  return async (requestId: string, currentName: string) => {
    const next = await prompt({
      title: "Rename request",
      placeholder: "Request name",
      initialValue: currentName,
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (!next || next === currentName) return;
    await api.requests.update(requestId, { name: next, note: "renamed" });
    qc.invalidateQueries({ queryKey: ["request", requestId] });
    qc.invalidateQueries({ queryKey: ["requests"] });
    qc.invalidateQueries({ queryKey: ["revisions", requestId] });
  };
}

// Webhook + flow have no name field in their builder anymore, so rename
// goes through the tab pill (double-click) and the sidebar row menu.
export function useRenameWebhook() {
  const prompt = usePrompt();
  const qc = useQueryClient();
  return async (webhookId: string, currentName: string) => {
    const next = await prompt({
      title: "Rename webhook",
      placeholder: "Webhook name",
      initialValue: currentName,
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (!next || next === currentName) return;
    await api.webhooks.update(webhookId, { name: next });
    qc.invalidateQueries({ queryKey: ["webhook", webhookId] });
    qc.invalidateQueries({ queryKey: ["webhooks"] });
  };
}

export function useRenameFlow() {
  const prompt = usePrompt();
  const qc = useQueryClient();
  return async (flowId: string, currentName: string) => {
    const next = await prompt({
      title: "Rename flow",
      placeholder: "Flow name",
      initialValue: currentName,
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (!next || next === currentName) return;
    await api.flows.update(flowId, { name: next });
    qc.invalidateQueries({ queryKey: ["flow", flowId] });
    qc.invalidateQueries({ queryKey: ["flows"] });
  };
}
